import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormTextField from 'components/ui/form/FormTextField';
import useFormSubmit from 'hooks/useFormSubmit';

const validationSchema = yup.object({
  name: yup.string().required('settings.crm.validation.name'),
  app_id: yup.string().required('settings.crm.fb.validation.appId'),
  app_secret: yup.string().required('settings.crm.fb.validation.appSecret'),
  short_token: yup.string().required('settings.crm.fb.validation.shortToken'),
});

const FbConnectModal = (props) => {
  const { open, setOpen, refetch = () => {}, data } = props;
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      name: data?.name ?? '',
      app_id: data?.app_id ?? '',
      app_secret: '',
      short_token: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const body = data?.id ? { ...values, facebook_id: data.id } : values;
      submit(
        { type: 'post', contentType: 'simple' },
        body,
        '/admin/facebook/connect',
        values.name,
        null,
        true,
        handleFinishRequest,
      );
    },
  });

  const handleFinishRequest = () => {
    refetch();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
    >
      <DialogTitle id='alert-dialog-title'>
        <span>{t('settings.crm.fb.connectTitle')}</span>
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3, lg: 3 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12, lg: 12 }}
          >
            <Grid item lg={12} sm={12} xs={12}>
              <FormTextField
                duration={0}
                label={t('common.fields.name')}
                fieldName='name'
                formik={formik}
              />
            </Grid>
            <Grid item lg={12} sm={12} xs={12}>
              <FormTextField
                duration={0}
                label={t('settings.crm.fb.appId')}
                fieldName='app_id'
                formik={formik}
              />
            </Grid>
            <Grid item lg={12} sm={12} xs={12}>
              <FormTextField
                duration={0}
                label={t('settings.crm.fb.appSecret')}
                fieldName='app_secret'
                formik={formik}
                type='password'
              />
            </Grid>
            <Grid item lg={12} sm={12} xs={12}>
              <FormTextField
                duration={0}
                label={t('settings.crm.fb.shortToken')}
                fieldName='short_token'
                formik={formik}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item lg={12} sm={12} xs={12}>
              <p className='text-xs text-gray-500'>
                {t('settings.crm.fb.connectHint')}
              </p>
            </Grid>
            <Grid item sm={12} xs={12}>
              <FormActionButtons
                duration={0}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={handleClose}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(FbConnectModal);
